import React, { useEffect, useState } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../components/firebase";
import "./WildlifeThreats.css";
import "./layout.css";

const WildlifeThreats = () => {
  const [allAlerts, setAllAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedThreatType, setSelectedThreatType] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [showLiveStream, setShowLiveStream] = useState(false);

  // Alert collections mapping
  const alertCollections = [
    'drone-bushfire-alerts',
    'drone-deforestation-alerts', 
    'drone-poaching-alerts',
    'iot-bushfire-alerts',
    'iot-deforestation-alerts',
    'iot-poaching-alerts'
  ];

  // Threat type filters with image icons
  const threatTypes = [
    { key: 'all', label: 'All Threats', icon: <img src="/all.jpeg" alt="All" style={{ width: 24, height: 24 }} /> },
    { key: 'bushfires', label: 'Bushfires', icon: <img src="/fire.png" alt="Bushfires" style={{ width: 24, height: 24 }} /> },
    { key: 'deforestation', label: 'Deforestation', icon: <img src="/forest.png" alt="Deforestation" style={{ width: 24, height: 24 }} /> },
    { key: 'poaching', label: 'Poaching', icon: <img src="/hunt.png" alt="Poaching" style={{ width: 24, height: 24 }} /> }
  ];

  // Source filters
  const sourceTypes = [
    { key: 'all', label: 'All Sources' },
    { key: 'drone', label: 'Drone Alerts' },
    { key: 'iot', label: 'IoT Alerts' }
  ];

  useEffect(() => {
    const unsubscribes = [];
    
    alertCollections.forEach(collectionName => {
      const q = query(collection(db, collectionName), orderBy("timestamp", "desc"));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const collectionData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          source: collectionName.startsWith('drone-') ? 'drone' : 'iot',
          collectionName: collectionName
        }));
        
        setAllAlerts(prevAlerts => {
          const filteredPrev = prevAlerts.filter(alert => alert.collectionName !== collectionName);
          const newAlerts = [...filteredPrev, ...collectionData].sort((a, b) => {
            const aTime = a.timestamp?.toDate?.() || new Date(0);
            const bTime = b.timestamp?.toDate?.() || new Date(0);
            return bTime - aTime;
          });
          return newAlerts;
        });
      }, (error) => {
        console.error(`Error fetching from ${collectionName}:`, error);
      });
      
      unsubscribes.push(unsubscribe);
    });

    return () => unsubscribes.forEach(unsub => unsub());
  }, []);

  // Filter alerts based on selected filters
  useEffect(() => {
    let filtered = allAlerts.filter((alert) => {
      return !alert.hasOwnProperty('confidence') || alert.confidence === undefined || alert.confidence > 0.24;
    });

    if (selectedThreatType !== 'all') {
      // A more robust check for threat type
      filtered = filtered.filter(alert => 
        alert.type?.toLowerCase().includes(selectedThreatType.slice(0, -1))
      );
    }

    if (selectedSource !== 'all') {
      filtered = filtered.filter(alert => alert.source === selectedSource);
    }

    setFilteredAlerts(filtered);
    
    // Auto-select the first alert in the filtered list or clear selection
    if (selectedAlert && !filtered.find(a => a.id === selectedAlert.id && a.collectionName === selectedAlert.collectionName)) {
      setSelectedAlert(filtered.length > 0 ? filtered[0] : null);
    } else if (!selectedAlert && filtered.length > 0) {
      setSelectedAlert(filtered[0]);
    }
  }, [allAlerts, selectedThreatType, selectedSource]);

  // Reset live stream when selected alert changes
  useEffect(() => {
    setShowLiveStream(false);
  }, [selectedAlert]);


  return (
    <div className="page-container">
      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-container">
          <div className="filter-group">
            <h4 className="filter-title">Threat Types</h4>
            <div className="filter-buttons">
              {threatTypes.map((threat) => (
                <button
                  key={threat.key}
                  className={`filter-btn ${selectedThreatType === threat.key ? 'active' : ''}`}
                  onClick={() => setSelectedThreatType(threat.key)}
                >
                  <span className="filter-icon">{threat.icon}</span>
                  <span className="filter-label">{threat.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h4 className="filter-title">Data Sources</h4>
            <div className="source-tabs">
              {sourceTypes.map((source) => (
                <button
                  key={source.key}
                  className={`source-tab ${selectedSource === source.key ? 'active' : ''}`}
                  onClick={() => setSelectedSource(source.key)}
                >
                  {source.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="results-info">
          <span className="results-count">
            {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''} found
          </span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="alerts-content">
        <div className="sidebar-alerts scrollable-alerts">
          <h4 className="bold-title">Threat Alerts</h4>
          {filteredAlerts.length === 0 && <p>No alerts found for selected filters.</p>}
          {filteredAlerts.map((alert) => (
            <div
              key={`${alert.collectionName}-${alert.id}`}
              className={`${getCardClass(alert.type)} ${selectedAlert?.id === alert.id && selectedAlert?.collectionName === alert.collectionName ? "selected" : ""}`}
              onClick={() => setSelectedAlert(alert)}
            >
              <div className="alert-card-header">
                <img src={getIconUrl(alert.type)} alt={alert.type} className="alert-icon" />
                <span className={`source-badge ${alert.source}`}>
                  {alert.source.toUpperCase()}
                </span>
              </div>
              <div className="alert-content">
                <h4>{capitalize(alert.type)}</h4>
                <p>Location: {alert.zone || 'N/A'}</p>
                <p>{formatTime(alert.timestamp)}</p>
                {alert.confidence !== undefined && (
                  <p className="confidence-text">
                    Confidence: {(alert.confidence * 100).toFixed(1)}%
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="main-content">
          <div className="alert-details">
            <h4 className="bold-title">Alert Details</h4>
            {selectedAlert ? (
              <>
                <div className="alert-detail-header">
                  <span className={`source-badge large ${selectedAlert.source}`}>
                    {selectedAlert.source.toUpperCase()} Alert
                  </span>
                  <span className="collection-name">
                    {selectedAlert.collectionName}
                  </span>
                </div>
                
                {selectedAlert.source === 'iot' && (
                  <div className="live-stream-container">
                    <button 
                      className={`stream-toggle-btn ${showLiveStream ? 'active' : ''}`}
                      onClick={() => setShowLiveStream(!showLiveStream)}
                    >
                      {showLiveStream ? 'Hide Live Stream' : 'Show Live Stream'}
                    </button>
                  </div>
                )}

                {showLiveStream && selectedAlert.source === 'iot' ? (
                  <div className="live-stream-wrapper">
                    <iframe
                      src={`http://your-streaming-service.com/live/${selectedAlert.zone}`}
                      className="live-stream-frame"
                      title={`Live Stream - ${selectedAlert.zone}`}
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <img
                    src={selectedAlert.snapshot || getIconUrl(selectedAlert.type)}
                    alt="Alert Snapshot"
                    className="alert-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getIconUrl(selectedAlert.type);
                    }}
                  />
                )}

                <div className="info">
                  <h4>{capitalize(selectedAlert.type)}</h4>
                  <p><strong>Time:</strong> {formatTime(selectedAlert.timestamp)}</p>
                  <p><strong>Location:</strong> {selectedAlert.zone || 'N/A'}</p>
                  <p><strong>Severity:</strong> {selectedAlert.severity || 'N/A'}</p>
                  {selectedAlert.confidence !== undefined && <p><strong>Confidence:</strong> {(selectedAlert.confidence * 100).toFixed(1)}%</p>}
                  {selectedAlert.smoke_ppm !== undefined && <p><strong>Smoke:</strong> {selectedAlert.smoke_ppm} ppm</p>}
                  {selectedAlert.sound_level !== undefined && <p><strong>Sound:</strong> {selectedAlert.sound_level}</p>}
                  <p><strong>Source:</strong> {selectedAlert.source.toUpperCase()}</p>
                  <p><strong>Collection:</strong> {selectedAlert.collectionName}</p>
                </div>
              </>
            ) : (
              <p>No alert selected. Please select an alert from the list.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getIconUrl = (type) => {
  switch (type?.toLowerCase()) {
    case "bushfires":
    case "bushfire":
      return "/fire.png";
    case "poaching":
      return "/hunt.png";
    case "deforestation":
      return "/forest.png";
    default:
      return "https://img.icons8.com/emoji/48/warning-emoji.png";
  }
};

const getCardClass = (type) => {
  const baseClass = "alert-card";
  switch (type?.toLowerCase()) {
    case "bushfires":
    case "bushfire":
      return `${baseClass} bushfire-bg`;
    case "poaching":
      return `${baseClass} poaching-bg`;
    case "deforestation":
      return `${baseClass} deforestation-bg`;
    default:
      return baseClass;
  }
};

const capitalize = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const formatTime = (timestamp) => {
  try {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleString();
  } catch {
    return "Unknown";
  }
};

export default WildlifeThreats;